import React, { useContext, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getMediaImage } from '../../api/aws/s3';
import { companiesRef } from '../../api/firebase/firestore/company';
import { issuesRef } from '../../api/firebase/firestore/issue';
import { getMedias } from '../../api/firebase/firestore/media';
import { getUserInfo } from '../../api/firebase/firestore/user';
import { getPost } from '../../api/microcms/article';
import noImage from '../../components/assets/noImage.svg';
import {
  black,
  blackText,
  blueGrayText,
  cardBorder,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  adTypeList,
  categoryListForReference,
  funnelList,
  guaranteeFlgList,
  mainKpiList,
  mediaBudgetList,
  mediaMaterialUpdateList,
  mediaTargetList,
  mediaTypeList,
  paymentList,
  targetTypeList,
  useIndustry
} from '../../constants/Select';
import { screenType, select, status } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';

import ContactDrawer from './ContactDrawer';
import CustomLoading from './CustomLoading';
import CustomShowDetail from './CustomShowDetail';
import CustomSnackbar from './CustomSnackbar';

import type { ArticleType } from '../../api/microcms/article/articleType';
import type { SelectOption } from '../../constants/Select';
import type { GenderDataType } from './CustomShowDetail';

const styles = {
  card: {
    boxShadow: 'none',
    margin: {
      md: '24px',
      xs: '16px'
    }
  },
  cardBottomContent: {
    padding: {
      md: '0 40px',
      xs: '0 16px'
    }
  },
  cardTopContent: {
    padding: {
      md: '40px',
      xs: '32px 16px'
    },
    rowGap: '16px'
  },
  label: {
    backgroundColor: 'rgba(10, 126, 191, 0.08)',
    border: '1px solid ##0A7EBF',
    borderRadius: '4px',
    mr: '8px'
  },
  mediaName: {
    fontSize: {
      md: '28px',
      xs: '20px'
    },
    fontWeight: '700',
    mb: '16px'
  },
  sendButton: {
    '@media screen and (min-width:960px)': { width: '200px' }
  },
  tab: {
    '&.Mui-selected': {
      color: black,
      fontWeight: 700
    },
    color: blueGrayText,
    fontSize: '14px',
    maxWidth: '264px',
    width: { lg: '80%', md: '50%', xl: '100%', xs: '40%' }
  },
  tabs: {
    '& .MuiTabs-flexContainer': {
      justifyContent: { lg: 'center' }
    },
    '& .MuiTabs-indicator': {
      backgroundColor: primarycolor
    }
  },
  text: {
    '@media screen and (max-width:480px)': { fontSize: '12px' },
    color: black,
    fontSize: '14px',
    overflowWrap: 'anywhere'
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={styles.cardBottomContent}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
};

interface A11yProps {
  'aria-controls': string;
  id: string;
}

const a11yProps = (index: number): A11yProps => ({
  'aria-controls': `simple-tabpanel-${index}`,
  id: `simple-tab-${index}`
});

interface Props {
  mediaId: string | undefined;
  screen: number;
}

const MypageMediaDetail: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { mediaId, screen } = props;
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState(0);
  const mTypeList = mediaTypeList();
  const cList = categoryListForReference();
  const fList = funnelList();
  const tList = mediaTargetList();
  const tTypeList = targetTypeList();
  const aTypeList = adTypeList();
  const bList = mediaBudgetList();
  const gList = guaranteeFlgList();
  const iList = useIndustry();
  const kList = mainKpiList();
  const pList = paymentList();
  const mMList = mediaMaterialUpdateList();
  const [data, setData] = React.useState({
    adOption: '',
    adType: [''],
    budget: '',
    castingFlg: '',
    categories: [''],
    clientsBenefits: '',
    continuousFactors: [''],
    demographicsArea: [0, 0, 0, 0, 0, 0, 0],
    demographicsDevice: [0, 0, 0],
    demographicsFemale: [0, 0, 0, 0, 0, 0, 0, 0],
    demographicsGender: [0, 0],
    demographicsGeneration: [0, 0, 0, 0, 0, 0, 0],
    demographicsMale: [0, 0, 0, 0, 0, 0, 0, 0],
    demographicsOther: '',
    draftFlg: true,
    dueDate: '',
    editorFlg: '',
    facebook: 0,
    funnel: fList,
    guarantee: '',
    guaranteeFlg: '',
    holdingData: '',
    holdingDataFlg: '',
    image: '',
    industry: [
      {
        industriesOther: '',
        industry: '',
        percentage: 0
      },
      {
        industriesOther: '',
        industry: '',
        percentage: 0
      },
      {
        industriesOther: '',
        industry: '',
        percentage: 0
      }
    ],
    instagram: 0,
    line: 0,
    mainKpi: [''],
    mediaMaterialUpdateCycle: '',
    mediaMenuUrl: '',
    mediaType: '',
    name: '',
    note: 0,
    otherStrength: '',
    overview: '',
    payment: [''],
    paymentFlg: '',
    reportFlg: '',
    researchSchemeFlg: '',
    scaleEmailSubscribers: 0,
    scaleMembers: 0,
    scalePvMau: 0,
    scaleUu: 0,
    seasonalMenuMaterialText: '',
    secondaryUseFlg: '',
    similarMedia: [
      {
        comparison: '',
        mediaName: ''
      },
      {
        comparison: '',
        mediaName: ''
      }
    ],
    target: [''],
    targetType: '',
    tiktok: 0,
    twitter: 0,
    usersBenefits: '',
    youtube: 0
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [articleData, setArticleData] = React.useState<ArticleType | null>(
    null
  );
  const [articleImage, setArticleImage] = useState<string>('');

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setValue(newValue);
  };

  const showValueToName = (
    selectOption: SelectOption[],
    itemValue: number | undefined,
    otherText?: string | null
  ): string => {
    if (itemValue === select.other && selectOption === kList) {
      return otherText ?? '';
    }

    const d = selectOption.find((element) => element.value === itemValue);
    return d?.option ?? '';
  };

  const showMediaType = (
    v: number
  ): { mediaTypeText: string; onlyMediaTypeText: string } => {
    let mediaTypeGroupName;
    let mediaTypeName;
    for (const group of mTypeList) {
      for (const option of group.options) {
        if (option.value === v) {
          mediaTypeGroupName = group.name;
          mediaTypeName = option.option;
        }
      }
    }
    const mediaTypeText = `${mediaTypeGroupName ?? ''} ${
      mediaTypeGroupName && mediaTypeName ? '＞' : ''
    } ${mediaTypeName ?? ''}`;
    return {
      mediaTypeText: mediaTypeText ?? '',
      onlyMediaTypeText: mediaTypeName ?? ''
    };
  };

  const [onlyMediaTypeText, setOnlyMediaTypeText] = useState<string>('');

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (mediaId) {
        const mediaData = await getMedias(mediaId);
        if (mediaData) {
          let categoryArray: string[] = [];
          if (typeof mediaData.category === 'number') {
            const text = showValueToName(cList, mediaData.category);
            categoryArray.push(text);
          } else if (Array.isArray(mediaData.category)) {
            categoryArray = mediaData.category.map((item) =>
              showValueToName(cList, item)
            );
          }
          const updateChecked = (
            list: SelectOption[],
            val: number[] | undefined
          ): SelectOption[] => {
            list.forEach((item) => {
              if (val?.includes(item.value)) {
                item.checked = true;
              }
            });
            return list;
          };
          const targetArray = mediaData.target?.map((item: number) =>
            showValueToName(tList, item)
          );
          const image = await getMediaImage(mediaData.image);

          const mediaType = showMediaType(mediaData.mediaType).mediaTypeText;
          setOnlyMediaTypeText(
            showMediaType(mediaData.mediaType).onlyMediaTypeText
          );

          const adTypeArray = mediaData.adType?.map((item: number) =>
            showValueToName(aTypeList, item)
          );
          const budget = showValueToName(bList, mediaData.budget);
          const guaranteeCheck = (
            guaranteeFlg: number | number[] | undefined
          ): string[] => {
            if (guaranteeFlg) {
              return typeof guaranteeFlg === 'number'
                ? [showValueToName(gList, guaranteeFlg)]
                : guaranteeFlg?.map((item: number) =>
                    showValueToName(gList, item)
                  );
            }
            return [];
          };
          const guaranteeFlg = guaranteeCheck(mediaData.guaranteeFlg);
          const industryArray = mediaData.industries?.map(
            (item: {
              industriesNum: number;
              industriesOther: string | null;
              percentage: number;
            }) => {
              if (item.industriesNum !== 99) {
                return {
                  industriesOther: '',
                  industry: showValueToName(iList, item.industriesNum),
                  percentage: item.percentage
                };
              }
              return {
                industriesOther: item.industriesOther ?? '',
                industry: item.industriesOther ?? '',
                percentage: item.percentage
              };
            }
          );
          const mainKpi = mediaData.mainKpi?.map((item: number) =>
            showValueToName(kList, item, mediaData.mainKpiOther)
          );

          let payment: string[] = [];
          if (Array.isArray(mediaData.payment)) {
            payment = mediaData.payment?.map((item: number) =>
              showValueToName(pList, item)
            );
          } else {
            payment = [];
          }

          const getSimilarMediaItem = (
            index: number
          ): {
            comparison: string;
            mediaName: string;
          } => ({
            comparison: mediaData.similarMedia?.[index]?.comparison || '',
            mediaName: mediaData.similarMedia?.[index]?.mediaName || ''
          });

          const articleId = mediaData.articleId;

          if (articleId) {
            const post = await getPost(articleId);
            setArticleData(post);
            const articleImagePath = await getMediaImage(
              post.imagePath as unknown as string
            );
            setArticleImage(articleImagePath ?? noImage);
          }

          setData({
            adOption: mediaData.adOption,
            adType: adTypeArray,
            budget,
            castingFlg:
              mediaData.castingFlg === undefined
                ? ''
                : mediaData.castingFlg
                ? t('media.boolOption.true')
                : t('media.boolOption.false'),
            categories: categoryArray,
            clientsBenefits: mediaData.clientsBenefits,
            continuousFactors: mediaData.continuousFactors,
            demographicsArea: [
              mediaData.demographicsArea?.hokkaidou ?? 0,
              mediaData.demographicsArea?.touhoku ?? 0,
              mediaData.demographicsArea?.kantou ?? 0,
              mediaData.demographicsArea?.chubu ?? 0,
              mediaData.demographicsArea?.kinki ?? 0,
              mediaData.demographicsArea?.chugoku ?? 0,
              mediaData.demographicsArea?.shikoku ?? 0,
              mediaData.demographicsArea?.kyushu ?? 0
            ],
            demographicsDevice: [
              mediaData.demographicsDevice?.pc ?? 0,
              mediaData.demographicsDevice?.sp ?? 0,
              mediaData.demographicsDevice?.tablet ?? 0
            ],
            demographicsFemale: [
              mediaData.demographicsGeneration?.f10 || 0,
              mediaData.demographicsGeneration?.f20 || 0,
              mediaData.demographicsGeneration?.f30 || 0,
              mediaData.demographicsGeneration?.f40 || 0,
              mediaData.demographicsGeneration?.f50 || 0,
              mediaData.demographicsGeneration?.f60 || 0,
              mediaData.demographicsGeneration?.f70 || 0,
              (mediaData.demographicsGeneration?.m10 || 0) +
                (mediaData.demographicsGeneration?.m20 || 0) +
                (mediaData.demographicsGeneration?.m30 || 0) +
                (mediaData.demographicsGeneration?.m40 || 0) +
                (mediaData.demographicsGeneration?.m50 || 0) +
                (mediaData.demographicsGeneration?.m60 || 0) +
                (mediaData.demographicsGeneration?.m70 || 0)
            ],
            demographicsGender: [
              mediaData.demographicsGender?.male || 0,
              mediaData.demographicsGender?.female || 0
            ],
            demographicsGeneration: [
              mediaData.demographicsGeneration?.f10 +
                mediaData.demographicsGeneration?.m10,
              mediaData.demographicsGeneration?.f20 +
                mediaData.demographicsGeneration?.m20,
              mediaData.demographicsGeneration?.f30 +
                mediaData.demographicsGeneration?.m30,
              mediaData.demographicsGeneration?.f40 +
                mediaData.demographicsGeneration?.m40,
              mediaData.demographicsGeneration?.f50 +
                mediaData.demographicsGeneration?.m50,
              mediaData.demographicsGeneration?.f60 +
                mediaData.demographicsGeneration?.m60,
              mediaData.demographicsGeneration?.f70 +
                mediaData.demographicsGeneration?.m70
            ],
            demographicsMale: [
              mediaData.demographicsGeneration?.m10 || 0,
              mediaData.demographicsGeneration?.m20 || 0,
              mediaData.demographicsGeneration?.m30 || 0,
              mediaData.demographicsGeneration?.m40 || 0,
              mediaData.demographicsGeneration?.m50 || 0,
              mediaData.demographicsGeneration?.m60 || 0,
              mediaData.demographicsGeneration?.m70 || 0,
              (mediaData.demographicsGeneration?.f10 || 0) +
                (mediaData.demographicsGeneration?.f20 || 0) +
                (mediaData.demographicsGeneration?.f30 || 0) +
                (mediaData.demographicsGeneration?.f40 || 0) +
                (mediaData.demographicsGeneration?.f50 || 0) +
                (mediaData.demographicsGeneration?.f60 || 0) +
                (mediaData.demographicsGeneration?.f70 || 0)
            ],
            demographicsOther: mediaData.demographicsOther,
            draftFlg: mediaData.draftFlg,
            dueDate: mediaData.dueDate,
            editorFlg:
              mediaData.editorFlg === undefined
                ? ''
                : mediaData.editorFlg
                ? t('media.boolOption.true')
                : t('media.boolOption.false'),
            facebook: mediaData.scaleSnsFollowers?.Facebook ?? '',
            funnel: updateChecked(fList, mediaData.funnel),
            guarantee:
              mediaData.guarantee === undefined
                ? ''
                : mediaData.guarantee
                ? t('media.boolOption.true')
                : t('media.boolOption.false'),
            guaranteeFlg: guaranteeFlg
              ?.map((s) => s.replace('保証あり', ''))
              .join('・'),
            holdingData: mediaData.holdingData,
            holdingDataFlg:
              mediaData.holdingDataFlg === undefined &&
              mediaData.holdingData === undefined
                ? ''
                : mediaData.holdingDataFlg || mediaData.holdingData
                ? t('media.boolOption.true')
                : t('media.boolOption.false'),
            image: image ?? noImage,
            industry: industryArray,
            instagram: mediaData.scaleSnsFollowers?.Instagram ?? '',
            line: mediaData.scaleSnsFollowers?.Line ?? '',
            mainKpi,
            mediaMaterialUpdateCycle: showValueToName(
              mMList,
              mediaData.mediaMaterialUpdateCycle
            ),
            mediaMenuUrl: mediaData.mediaMenuUrl,
            mediaType,
            name: mediaData.name,
            note: mediaData.scaleSnsFollowers?.note ?? '',
            otherStrength: mediaData.otherStrength,
            overview: mediaData.overview,
            payment,
            paymentFlg:
              mediaData.paymentFlg === undefined
                ? ''
                : mediaData.paymentFlg
                ? t('media.boolOption.true')
                : t('media.boolOption.false'),
            reportFlg:
              mediaData.reportFlg === undefined
                ? ''
                : mediaData.reportFlg
                ? t('media.boolOption.true')
                : t('media.boolOption.false'),
            researchSchemeFlg:
              mediaData.researchSchemeFlg === undefined
                ? ''
                : mediaData.researchSchemeFlg
                ? t('media.boolOption.true')
                : t('media.boolOption.false'),
            scaleEmailSubscribers:
              typeof mediaData.scaleEmailSubscribers === 'number'
                ? mediaData.scaleEmailSubscribers
                : 0,
            scaleMembers:
              typeof mediaData.scaleMembers === 'number'
                ? mediaData.scaleMembers
                : 0,
            scalePvMau:
              typeof mediaData.scalePvMau === 'number'
                ? mediaData.scalePvMau
                : 0,
            scaleUu:
              typeof mediaData.scaleUu === 'number' ? mediaData.scaleUu : 0,
            seasonalMenuMaterialText: mediaData.seasonalMenuMaterialText,
            secondaryUseFlg:
              mediaData.secondaryUseFlg === undefined
                ? ''
                : mediaData.secondaryUseFlg
                ? t('media.possibleOption.true')
                : t('media.possibleOption.false'),
            similarMedia: [getSimilarMediaItem(0), getSimilarMediaItem(1)],
            target: targetArray,
            targetType: showValueToName(tTypeList, mediaData.targetType),
            tiktok: mediaData.scaleSnsFollowers?.TikTok ?? '',
            twitter: mediaData.scaleSnsFollowers?.Twitter ?? '',
            usersBenefits: mediaData.value,
            youtube: mediaData.scaleSnsFollowers?.Youtube ?? ''
          });
          setMediaMaterialFilePath(mediaData.mediaMaterialFile);
          setOtherMaterialFilePath(mediaData.otherMaterialFile);
          setSeasonalMenuMaterialFilePath(mediaData.seasonalMenuMaterialFile);
          setGenderShowData({
            data: {
              datasets: [
                {
                  backgroundColor: genderColor,
                  borderColor: genderColor,
                  data: [
                    mediaData.demographicsGender?.male,
                    mediaData.demographicsGender?.female
                  ],
                  label: 'Dataset'
                }
              ],
              labels: [
                `${t('media.genderOption.male')}：${
                  mediaData.demographicsGender?.male
                }%`,
                `${t('media.genderOption.female')}${
                  mediaData.demographicsGender?.female
                }%`
              ]
            },
            genderFemaleButton,
            genderMaleButton,
            handleFemaleButton,
            handleMaleButton,
            title: t('media.gender')
          });
        }
      }
      setLoading(false);
    })();
  }, [mediaId]);

  const [mediaMaterialFilePath, setMediaMaterialFilePath] =
    useState<string[]>();
  const [otherMaterialFilePath, setOtherMaterialFilePath] =
    useState<string[]>();
  const [seasonalMenuMaterialFilePath, setSeasonalMenuMaterialFilePath] =
    useState<string[]>();

  const getFileName = (filePath: string[] | undefined): string => {
    if (filePath && filePath.length > 0) {
      const regex = /(\d{13}_)(.*)/;
      const matches = filePath[0].match(regex);

      const extractedString = matches?.[2];
      return extractedString ?? '';
    }
    return '';
  };

  const mediaMaterialFileName = getFileName(mediaMaterialFilePath);
  const otherMaterialFileName = getFileName(otherMaterialFilePath);
  const seasonalMenuMaterialFileName = getFileName(
    seasonalMenuMaterialFilePath
  );

  const [genderMaleButton, setGenderMaleButton] = useState<boolean>(false);
  const [genderFemaleButton, setGenderFemaleButton] = useState<boolean>(false);
  const genderColor = ['#770079', '#A215B8'];
  const genderDetailColor = [
    '#770079',
    '#A215B8',
    '#C233D8',
    '#D652EB',
    '#ED7BFF',
    '#F6B9FF',
    '#FBE0FF',
    '#DBDBE0'
  ];

  useEffect(() => {
    updateGenderData(genderMaleButton, genderFemaleButton);
  }, [genderMaleButton, genderFemaleButton]);

  const handleMaleButton = (): void => {
    setGenderMaleButton((prevMale) => {
      const newMale = !prevMale;
      const newFemale = newMale ? false : genderFemaleButton;
      setGenderFemaleButton(newFemale);
      return newMale;
    });
  };

  const handleFemaleButton = (): void => {
    setGenderFemaleButton((prevFemale) => {
      const newFemale = !prevFemale;
      const newMale = newFemale ? false : genderMaleButton;
      setGenderMaleButton(newMale);
      return newFemale;
    });
  };

  const [genderShowData, setGenderShowData] = useState<GenderDataType>({
    data: {
      datasets: [
        {
          backgroundColor: genderColor,
          borderColor: genderColor,
          data: data.demographicsGender,
          label: 'Dataset'
        }
      ],
      labels: [
        `${t('media.genderOption.male')}：0}%`,
        `${t('media.genderOption.female')}：0%`
      ]
    },
    genderFemaleButton,
    genderMaleButton,
    handleFemaleButton,
    handleMaleButton,
    title: t('media.gender')
  });

  const updateGenderData = (male: boolean, female: boolean): void => {
    if (male && !female) {
      setGenderShowData({
        data: {
          datasets: [
            {
              backgroundColor: genderDetailColor,
              borderColor: genderDetailColor,
              data: data.demographicsMale,
              label: 'Dataset'
            }
          ],
          labels: [
            `${t('media.generationOption.10')}：${data.demographicsMale[0]}%`,
            `${t('media.generationOption.20')}：${data.demographicsMale[1]}%`,
            `${t('media.generationOption.30')}：${data.demographicsMale[2]}%`,
            `${t('media.generationOption.40')}：${data.demographicsMale[3]}%`,
            `${t('media.generationOption.50')}：${data.demographicsMale[4]}%`,
            `${t('media.generationOption.60')}：${data.demographicsMale[5]}%`,
            `${t('media.generationOption.70')}：${data.demographicsMale[6]}%`,
            `${t('media.genderOption.female')}：${data.demographicsMale[7]}%`
          ]
        },
        genderFemaleButton: female,
        genderMaleButton: male,
        handleFemaleButton,
        handleMaleButton,
        title: t('media.gender')
      });
    } else if (!male && female) {
      setGenderShowData({
        data: {
          datasets: [
            {
              backgroundColor: genderDetailColor,
              borderColor: genderDetailColor,
              data: data.demographicsFemale,
              label: 'Dataset'
            }
          ],
          labels: [
            `${t('media.generationOption.10')}：${data.demographicsFemale[0]}%`,
            `${t('media.generationOption.20')}：${data.demographicsFemale[1]}%`,
            `${t('media.generationOption.30')}：${data.demographicsFemale[2]}%`,
            `${t('media.generationOption.40')}：${data.demographicsFemale[3]}%`,
            `${t('media.generationOption.50')}：${data.demographicsFemale[4]}%`,
            `${t('media.generationOption.60')}：${data.demographicsFemale[5]}%`,
            `${t('media.generationOption.70')}：${data.demographicsFemale[6]}%`,
            `${t('media.genderOption.male')}：${data.demographicsFemale[7]}%`
          ]
        },
        genderFemaleButton: female,
        genderMaleButton: male,
        handleFemaleButton,
        handleMaleButton,
        title: t('media.gender')
      });
    } else {
      setGenderShowData({
        data: {
          datasets: [
            {
              backgroundColor: genderColor,
              borderColor: genderColor,
              data: data.demographicsGender,
              label: 'Dataset'
            }
          ],
          labels: [
            `${t('media.genderOption.male')}：${data.demographicsGender[0]}%`,
            `${t('media.genderOption.female')}${data.demographicsGender[1]}%`
          ]
        },
        genderFemaleButton: female,
        genderMaleButton: male,
        handleFemaleButton,
        handleMaleButton,
        title: t('media.gender')
      });
    }
  };

  const getNameByMediaType = (mediaType: string): string | undefined => {
    const optionsList = mediaTypeList();
    for (const group of optionsList) {
      for (const option of group.options) {
        if (option.option === mediaType) {
          return group.name;
        }
      }
    }
    return undefined;
  };

  // 月間PV数のタイトルをメディア種別によって出し分け
  const pvTextSwitcher = (mediaType: string): string => {
    const mediaTypeName = getNameByMediaType(mediaType);
    if (
      mediaTypeName === t('media.mediaTypeOption.tv') ||
      mediaTypeName === t('media.mediaTypeOption.radio')
    ) {
      return t('media.scaleDetail.sacleNet');
    } else if (
      mediaTypeName === t('media.mediaTypeOption.paper') ||
      mediaTypeName === t('media.mediaTypeOption.magazine')
    ) {
      return t('media.scaleDetail.scaleCirculation');
    } else if (mediaTypeName === t('media.mediaTypeOption.web')) {
      return t('media.scaleDetail.scalePvMau');
    }
    return t('media.scaleDetail.scaleOoh');
  };

  // 月間UU数のタイトルをメディア種別によって出し分け
  const uuTextSwitcher = (mediaType: string): string => {
    const mediaTypeName = getNameByMediaType(mediaType);
    if (
      mediaTypeName === t('media.mediaTypeOption.tv') ||
      mediaTypeName === t('media.mediaTypeOption.radio')
    ) {
      return '';
    } else if (
      mediaTypeName === t('media.mediaTypeOption.paper') ||
      mediaTypeName === t('media.mediaTypeOption.magazine')
    ) {
      return t('media.scaleDetail.scalReaders');
    } else if (mediaTypeName === t('media.mediaTypeOption.web')) {
      return t('media.scaleDetail.scaleUu');
    }
    return '';
  };

  // お問い合わせ
  const { currentUser } = useContext(AuthContext);
  const [toggleDrawer, setToggleDrawer] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [cantInquiry, setCantInquiry] = useState<string | undefined>(undefined);
  const handleDrawerToggle = (): void => {
    setToggleDrawer(!toggleDrawer);
  };

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        if (screen === screenType.publisher) return;
        const userData = await getUserInfo(currentUser.uid);
        const comId = userData?.companyId as string;
        const comRef = doc(companiesRef, comId);
        const comSnap = await getDoc(comRef);
        const comData = comSnap.data();
        // 会社が未承認の場合は、問い合わせNG
        if (comData?.status !== status.approved) {
          setCantInquiry(t('media.unApprovedCompany'));
        } else {
          let q;
          if (screen === screenType.client) {
            q = query(issuesRef, where('companyId', '==', comId));
          } else {
            q = query(issuesRef, where('agentId', '==', comId));
          }
          const { empty, docs } = await getDocs(q);
          if (empty) {
            setCantInquiry(t('media.noIssue'));
          } else {
            const approvedDocsCount = docs.filter(
              (document) => document.data().status === status.approved
            ).length;
            if (approvedDocsCount === 0) {
              setCantInquiry(t('media.unApprovedIssue'));
            }
          }
        }
      }
    })();
  }, []);

  return (
    <>
      <CustomLoading loading={loading} />
      <Card sx={[cardBorder, styles.card]}>
        <Grid container flexDirection="column" sx={styles.cardTopContent}>
          <Grid item>
            {data.categories.map((category: string, i: number) => (
              <Chip
                key={i}
                sx={styles.label}
                label={category}
                color="primary"
                variant="outlined"
              />
            ))}
          </Grid>
          <Grid
            item
            container
            direction={{ sm: 'row', xs: 'column' }}
            justifyContent="space-between"
          >
            <Grid item>
              <Typography component="p" sx={[blackText, styles.mediaName]}>
                {data.name}
              </Typography>
            </Grid>
            {screen !== screenType.publisher && (
              <Grid item sx={styles.sendButton}>
                <Button
                  variant="contained"
                  sx={[
                    primaryButtonBackground,
                    {
                      flex: 1,
                      height: {
                        xs: '36px'
                      }
                    }
                  ]}
                  fullWidth
                  onClick={() => {
                    if (cantInquiry) {
                      setErrorText(cantInquiry);
                      setOpen(true);
                    } else {
                      handleDrawerToggle();
                    }
                  }}
                >
                  <Typography sx={[styles.text, { color: 'white' }]}>
                    {t('media.send')}
                  </Typography>
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
              variant="scrollable"
              scrollButtons={false}
              sx={styles.tabs}
            >
              <Tab
                label={t('media.basicInfo')}
                sx={styles.tab}
                {...a11yProps(0)}
              />
              <Tab
                label={t('media.userInfo')}
                sx={styles.tab}
                {...a11yProps(1)}
              />
              <Tab
                label={t('media.service')}
                sx={styles.tab}
                {...a11yProps(2)}
              />
              <Tab
                label={t('media.achievements')}
                sx={styles.tab}
                {...a11yProps(3)}
              />
            </Tabs>
          </Box>
          <TabPanel value={value} index={0}>
            <CustomShowDetail
              type="image"
              itemName={t('media.overview')}
              image={data.image ?? noImage}
              text={data.overview}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.mediaType')}
              text={data.mediaType}
            />
            <Divider />
            <CustomShowDetail
              type="tag"
              itemName={t('media.funnel')}
              arrayChips={data.funnel}
              guideText={t('guideText.mediaDetail.funnel')}
            />
            <Divider />
            <CustomShowDetail
              type="table"
              itemName={t('media.scale')}
              tableItems={[
                {
                  text: data.scalePvMau?.toLocaleString(),
                  title: pvTextSwitcher(onlyMediaTypeText)
                },
                {
                  hide: uuTextSwitcher(onlyMediaTypeText) === '',
                  text:
                    data.scaleUu !== 0 ? data.scaleUu?.toLocaleString() : '',
                  title: uuTextSwitcher(onlyMediaTypeText)
                },
                {
                  text:
                    data.scaleMembers !== 0
                      ? data.scaleMembers?.toLocaleString()
                      : '',
                  title: t('media.scaleDetail.scaleMembers')
                },
                {
                  text:
                    data.scaleEmailSubscribers !== 0
                      ? data.scaleEmailSubscribers?.toLocaleString()
                      : '',
                  title: t('media.scaleDetail.scaleEmailSubscribers')
                },
                {
                  array: [
                    {
                      text: data.facebook?.toLocaleString(),
                      title: 'facebook'
                    },
                    { text: data.twitter?.toLocaleString(), title: 'twitter' },
                    {
                      text: data.instagram?.toLocaleString(),
                      title: 'Instagram'
                    },
                    { text: data.tiktok?.toLocaleString(), title: 'Tiktok' },
                    { text: data.note?.toLocaleString(), title: 'note' },
                    { text: data.youtube?.toLocaleString(), title: 'Youtube' },
                    { text: data.line?.toLocaleString(), title: 'LINE' }
                  ],
                  title: t('media.snsFollower')
                }
              ]}
            />
            {!(data.draftFlg === undefined || data.draftFlg) && (
              <>
                <Divider />
                <CustomShowDetail
                  type="media"
                  articleData={articleData}
                  articleImagePath={articleImage}
                  itemName={t('mediaArticle.article')}
                  screen={screen}
                />
              </>
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            <CustomShowDetail
              type="table"
              itemName={t('media.targetTitle')}
              tableItems={[
                { text: data.target?.join('、'), title: t('media.target') },
                { text: data.targetType, title: t('media.targetType') },
                {
                  text: data.demographicsOther,
                  title: t('media.targetDetail')
                }
              ]}
            />
            <Divider />
            <CustomShowDetail
              type="graph"
              itemName={t('media.demographicInfo')}
              graphItems={[
                genderShowData,
                {
                  data: {
                    datasets: [
                      {
                        backgroundColor: [
                          '#1B0167',
                          '#623FC8',
                          '#815AF4',
                          '#9773FF',
                          '#BCA7F9',
                          '#DACDFF',
                          '#EBE4FF'
                        ],
                        borderColor: [
                          '#1B0167',
                          '#623FC8',
                          '#815AF4',
                          '#9773FF',
                          '#BCA7F9',
                          '#DACDFF',
                          '#EBE4FF'
                        ],
                        data: data.demographicsGeneration,
                        label: 'Dataset'
                      }
                    ],
                    labels: [
                      `${t('media.generationOption.10')}：${
                        data.demographicsGeneration[0]
                      }%`,
                      `${t('media.generationOption.20')}：${
                        data.demographicsGeneration[1]
                      }%`,
                      `${t('media.generationOption.30')}：${
                        data.demographicsGeneration[2]
                      }%`,
                      `${t('media.generationOption.40')}：${
                        data.demographicsGeneration[3]
                      }%`,
                      `${t('media.generationOption.50')}：${
                        data.demographicsGeneration[4]
                      }%`,
                      `${t('media.generationOption.60')}：${
                        data.demographicsGeneration[5]
                      }%`,
                      `${t('media.generationOption.70')}：${
                        data.demographicsGeneration[6]
                      }%`
                    ]
                  },
                  title: t('media.generation')
                },
                {
                  data: {
                    datasets: [
                      {
                        backgroundColor: [
                          '#016167',
                          '#057B83',
                          '#14A0A9',
                          '#13BECA',
                          '#26D7E3',
                          '#5FE6F0',
                          '#8BF7FF',
                          '#C0FBFF'
                        ],
                        borderColor: [
                          '#016167',
                          '#057B83',
                          '#14A0A9',
                          '#13BECA',
                          '#26D7E3',
                          '#5FE6F0',
                          '#8BF7FF',
                          '#C0FBFF'
                        ],
                        data: data.demographicsArea,
                        label: 'Dataset'
                      }
                    ],
                    labels: [
                      `${t('media.accessAreaOption.hokkaido')}：${
                        data.demographicsArea[0]
                      }%`,
                      `${t('media.accessAreaOption.tohoku')}：${
                        data.demographicsArea[1]
                      }%`,
                      `${t('media.accessAreaOption.kanto')}：${
                        data.demographicsArea[2]
                      }%`,
                      `${t('media.accessAreaOption.chubu')}：${
                        data.demographicsArea[3]
                      }%`,
                      `${t('media.accessAreaOption.kinki')}：${
                        data.demographicsArea[4]
                      }%`,
                      `${t('media.accessAreaOption.chugoku')}：${
                        data.demographicsArea[5]
                      }%`,
                      `${t('media.accessAreaOption.shikoku')}：${
                        data.demographicsArea[6]
                      }%`,
                      `${t('media.accessAreaOption.kyushu')}：${
                        data.demographicsArea[7]
                      }%`
                    ]
                  },
                  title: t('media.accessArea')
                },
                {
                  data: {
                    datasets: [
                      {
                        backgroundColor: ['#013067', '#0A57B1', '#4A94EC'],
                        borderColor: ['#013067', '#0A57B1', '#4A94EC'],
                        data: data.demographicsDevice,
                        label: 'Dataset'
                      }
                    ],
                    labels: [
                      `${t('media.deviceOption.pc')}：${
                        data.demographicsDevice[0]
                      }%`,
                      `${t('media.deviceOption.mobile')}：${
                        data.demographicsDevice[1]
                      }%`,
                      `${t('media.deviceOption.tablet')}：${
                        data.demographicsDevice[2]
                      }%`
                    ]
                  },
                  title: t('media.device')
                }
              ]}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.usersBenefit')}
              text={data.usersBenefits}
              guideText={t('guideText.mediaDetail.usersBenefit')}
            />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <CustomShowDetail
              type="text"
              itemName={t('media.adType')}
              text={data.adType?.join('、')}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.adOption')}
              text={data.adOption}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.otherStrength')}
              text={data.otherStrength}
            />
            <Divider />
            <CustomShowDetail
              type="table"
              itemName={t('media.holdingDataTitle')}
              tableItems={[
                {
                  text: data.holdingDataFlg,
                  title: t('media.holdingDataFlg')
                },
                { text: data.holdingData, title: t('media.holdingData') }
              ]}
            />
            <Divider />
            <CustomShowDetail
              type="table"
              itemName={t('media.boolEC')}
              tableItems={[
                { text: data.paymentFlg, title: t('media.paymentFlg') },
                {
                  text: data.payment?.join('、'),
                  title: t('media.payment')
                }
              ]}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.budget')}
              text={data.budget}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.dueDate')}
              text={data.dueDate}
            />
            <Divider />
            <CustomShowDetail
              type="table"
              itemName={t('media.supplementary')}
              tableRow={true}
              tableItems={[
                { text: data.editorFlg, title: t('media.editorFlg') },
                {
                  text: data.guaranteeFlg
                    ? `${data.guarantee} / ${data.guaranteeFlg}`
                    : `${data.guarantee}`,
                  title: t('media.guaranteeFlg')
                },
                { text: data.reportFlg, title: t('media.reportFlg') }
              ]}
              secondTableItems={[
                { text: data.castingFlg, title: t('media.castingFlg') },
                {
                  text: data.secondaryUseFlg,
                  title: t('media.secondaryUseFlg')
                },
                {
                  text: data.researchSchemeFlg,
                  title: t('media.researchSchemeFlg')
                }
              ]}
            />
            <Divider />
            <CustomShowDetail
              type="table"
              itemName={t('media.serviceComparison')}
              tableRow={false}
              tableItems={[
                {
                  text: data.similarMedia[0].mediaName,
                  title: t('media.serviceName')
                },
                {
                  text: data.similarMedia[0].comparison,
                  title: t('media.differenceAndCoexistence')
                }
              ]}
              secondTableItems={[
                {
                  text: data.similarMedia[1].mediaName,
                  title: t('media.serviceName')
                },
                {
                  text: data.similarMedia[1].comparison,
                  title: t('media.differenceAndCoexistence')
                }
              ]}
            />
            <Divider />
            <CustomShowDetail
              type="file"
              itemName={t('media.mediaMaterialUrl')}
              text={mediaMaterialFileName}
              path={mediaMaterialFilePath}
            />
            <Divider />
            <CustomShowDetail
              type="file"
              itemName={t('media.seasonalMenuMaterialUrl')}
              text={seasonalMenuMaterialFileName}
              path={seasonalMenuMaterialFilePath}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.seasonalMenuMaterialText')}
              text={data.seasonalMenuMaterialText}
            />
            <Divider />
            <CustomShowDetail
              type="file"
              itemName={t('media.otherMaterialUrl')}
              text={otherMaterialFileName}
              path={otherMaterialFilePath}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.mediaMaterialUpdateCycle')}
              text={data.mediaMaterialUpdateCycle}
            />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <CustomShowDetail
              type="orderList"
              itemName={t('media.industries')}
              orderList={
                data.industry
                  ? [
                      data.industry[0]?.percentage &&
                      data.industry[0]?.percentage >= 1
                        ? `${data.industry[0]?.industry} : ${data.industry[0]?.percentage}%`
                        : `${data.industry[0]?.industry}`,
                      data.industry[1]?.percentage &&
                      data.industry[1]?.percentage >= 1
                        ? `${data.industry[1]?.industry} : ${data.industry[1]?.percentage}%`
                        : `${data.industry[1]?.industry}`,
                      data.industry[2]?.percentage &&
                      data.industry[2]?.percentage >= 1
                        ? `${data.industry[2]?.industry} : ${data.industry[2]?.percentage}%`
                        : `${data.industry[2]?.industry}`
                    ]
                  : ['', '', '']
              }
              guideText={t('guideText.mediaDetail.industries')}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.mainKpi')}
              text={data.mainKpi?.join('、')}
              guideText={t('guideText.mediaDetail.mainKpi')}
            />
            <Divider />
            <CustomShowDetail
              type="text"
              itemName={t('media.clientBenefit')}
              text={data.clientsBenefits}
              guideText={t('guideText.mediaDetail.clientBenefit')}
            />
            <Divider />
            <CustomShowDetail
              type="orderList"
              itemName={t('media.continuousFactors')}
              orderList={data.continuousFactors}
            />
            <Divider />
            <CustomShowDetail
              type="url"
              itemName={t('media.mediaMenuUrl')}
              text={data.mediaMenuUrl}
            />
          </TabPanel>
        </Box>
      </Card>
      <ContactDrawer
        screen={screen}
        mediaId={mediaId ?? ''}
        toggleDrawer={toggleDrawer}
        handleDrawerToggle={handleDrawerToggle}
        onClose={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
          e.stopPropagation();
          handleDrawerToggle();
        }}
      />
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default MypageMediaDetail;
